import { useMemberSettings } from "@hooks/useMemberSettings";
import { loadSdkThemeCss } from "@utils/loadSdkThemeCss";
import logger from "@utils/logger";
import { useEffect } from "react";

/**
 * Load theme color from local storage
 * Apply theme to:
 * - body className
 * - bg-body background image
 * - meta theme-color
 */
const ThemeColorLoader = () => {
  const { themeId } = useMemberSettings();

  // update theme class to body
  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      const themeConfigs = import.meta.env.VITE_THEME_CONFIGS;
      let themeConfig = themeConfigs[themeId];

      // Fallback to purple-stardust if theme ID is unknown
      if (!themeConfig) {
        logger._console.error(
          `ThemeColorLoader: themeConfig not found for themeId "${themeId}", falling back to purple-stardust`,
        );
        themeConfig = themeConfigs["purple-stardust"];
        if (!themeConfig) {
          logger._console.error("ThemeColorLoader: purple-stardust fallback themeConfig not found");
          return;
        }
      }

      await loadSdkThemeCss(themeConfig.id);
      if (cancelled) return;

      // update theme extended css (e.g. background image variables)
      const styleId = "bg-variables-style";
      let styleEl = document.getElementById(styleId) as HTMLStyleElement | null;
      if (!styleEl) {
        styleEl = document.createElement("style");
        styleEl.type = "text/css";
        styleEl.id = styleId;
        document.head.appendChild(styleEl);
      }
      styleEl.textContent = themeConfig.extendedCss ?? "";
      document.querySelector("meta[name='theme-color']")?.setAttribute("content", themeConfig.metaThemeColor);

      const bodyClassList = Array.from(document.body.classList);
      const currentTheme = bodyClassList.find((cls) => cls.startsWith("sdk-theme-"));
      if (currentTheme === themeConfig.className) {
        logger._console.debug("ThemeColorLoader: themeColor is same as current theme, skipping");
        return;
      }

      document.body.classList.remove(
        ...Array.from(document.body.classList).filter((cls) => cls.startsWith("sdk-theme-")),
      );
      document.body.classList.add(themeConfig.className);
    };

    void run();

    return () => {
      cancelled = true;
    };
  }, [themeId]);

  return null;
};

export default ThemeColorLoader;
